/**
 * Auth store — Zustand for client-only UI state.
 * Server state (session, profile) lives in TanStack Query.
 */
import { create } from 'zustand'

interface AuthStoreState {
  isInitializing: boolean
  isCompletingSignup: boolean
  sessionExpired: boolean
  biometricPrompted: boolean
  biometricFailCount: number
  credentialUnlockNonce: number
  loginAttempts: number
  lastLoginAttempt: number
  /**
   * Transient phone number handed from a phone-entry step to the OTP step.
   *
   * PII — IN-MEMORY ONLY. This field MUST NEVER be persisted (this store
   * deliberately has no `persist` middleware, no MMKV and no SecureStore
   * backing) and MUST NEVER be passed through `router.push`/`router.replace`
   * params: expo-router serialises params into the query string on web, which
   * would leak the number into browser history, EAS Hosting access logs and
   * Sentry's browser-tracing URL capture. Non-PII flow flags (mode, channel)
   * stay in the URL so refresh/deep links keep working.
   *
   * Because it lives in memory only, a web refresh legitimately loses it —
   * consumers must fall back to sending the user back to the phone-entry
   * step rather than rendering an OTP screen that can never succeed.
   */
  pendingAuthPhone: string | null
  /**
   * Cuándo quedó viva la sesión actual (SIGNED_IN o INITIAL_SESSION con user).
   *
   * El controller de OTA lo lee para no recargar la app durante la ventana
   * posterior a una autenticación: `reloadAsync` es un reinicio completo
   * (splash + permisos), y hacerlo a los 500 ms de aterrizar en /explore se
   * ve exactamente como "el login se hizo dos veces".
   */
  lastAuthenticatedAt: number | null

  /**
   * Ruta esperando a que el gate de auth termine para navegar.
   *
   * Un tap de notificación en arranque en frío no puede `router.push` directo:
   * el redirect del root (welcome/explore) se la come — TODAS las
   * notificaciones aterrizaban en explore. El hook la etapa acá y RootLayoutApp
   * la consume una sola vez cuando la app está lista, igual que un deep link
   * nativo. En memoria, nunca persistida.
   */
  pendingNotificationRoute: string | null

  setInitializing: (v: boolean) => void
  setCompletingSignup: (v: boolean) => void
  setSessionExpired: (v: boolean) => void
  setBiometricPrompted: (v: boolean) => void
  markAuthenticated: (at: number) => void
  setPendingNotificationRoute: (route: string | null) => void
  markCredentialUnlock: () => void
  incrementBiometricFail: () => void
  resetBiometricFail: () => void
  incrementLoginAttempts: () => void
  resetLoginAttempts: () => void
  /** Stage the transient phone for the next OTP step. Pass null to clear. */
  setPendingAuthPhone: (phone: string | null) => void
  reset: () => void
}

const initialState = {
  isInitializing: true,
  isCompletingSignup: false,
  sessionExpired: false,
  biometricPrompted: false,
  biometricFailCount: 0,
  credentialUnlockNonce: 0,
  loginAttempts: 0,
  lastLoginAttempt: 0,
  pendingAuthPhone: null as string | null,
  lastAuthenticatedAt: null as number | null,
  pendingNotificationRoute: null as string | null,
}

export const useAuthStore = create<AuthStoreState>((set) => ({
  ...initialState,

  setInitializing: (v) => set({ isInitializing: v }),
  setCompletingSignup: (v) => set({ isCompletingSignup: v }),
  setSessionExpired: (v) => set({ sessionExpired: v }),
  setBiometricPrompted: (v) => set({ biometricPrompted: v }),
  markAuthenticated: (at) => set({ lastAuthenticatedAt: at }),
  setPendingNotificationRoute: (route) => set({ pendingNotificationRoute: route }),
  markCredentialUnlock: () =>
    set((state) => ({
      credentialUnlockNonce: state.credentialUnlockNonce + 1,
      biometricFailCount: 0,
    })),
  incrementBiometricFail: () =>
    set((state) => ({ biometricFailCount: state.biometricFailCount + 1 })),
  resetBiometricFail: () => set({ biometricFailCount: 0 }),
  incrementLoginAttempts: () =>
    set((state) => ({
      loginAttempts: state.loginAttempts + 1,
      lastLoginAttempt: Date.now(),
    })),
  resetLoginAttempts: () => set({ loginAttempts: 0, lastLoginAttempt: 0 }),
  setPendingAuthPhone: (phone) => set({ pendingAuthPhone: phone }),
  reset: () => set(initialState),
}))
